import { Badge, Card, Grid, Group, Text } from "@mantine/core"
import { createColumnHelper } from "@tanstack/react-table"
import React from "react"
import { Link } from "react-router-dom"
import { OperationsAdminVenueTalentTabs } from "src/components/operations/administrator/talent/OperationsAdminVenueTalentTabs"
import { CreateOperationsAdminVenueTalentGuidelineAction, UpdateOperationsAdminVenueTalentGuidelineAction } from "src/components/operations/administrator/talent/guidelines/OperationsAdminVenueTalentGuidelineActions"
import { DataTable } from "src/components/ui/DataTable"
import { LoadMoreButton } from "src/components/ui/LoadMoreButton"
import { EmptyPageComponent } from "src/components/ui/Misc"
import { ModuleErrors } from "src/components/ui/ModuleErrors"
import { PageFilters, usePageFilters } from "src/components/ui/PageFilters"
import { PageHeader } from "src/components/ui/PageHeader"
import { PageLayout } from "src/components/ui/PageLayout"
import { useActions, useAppState } from "src/state"
import { SetMetaInput } from "src/state/Module"
import { GuidelineFragment, Guideline_Status_Enum, Guidelines_Bool_Exp } from "src/state/gql/_types"
import { formatDate } from "src/utils/dates"
import { capitalizeWord } from "src/utils/string"

export function OperationsAdminVenueTalentGuidelines() {

    const { itemsList, errors, meta, loadingMore, loading, meta: { limit, offset, sortKey, sortOrder } } = useAppState(state => state.guidelines)
    const {
        subscribeToItems,
        unsubscribeFromItems,
        getMoreItems,
        resetModule,
        setMeta,
        selectItem
    } = useActions().guidelines
    const { view } = useAppState(state => state.ui)
    const { currentItemId: currentUserId } = useAppState(state => state.users)

    const {
        filters,
        filterParams
    } = usePageFilters<Guidelines_Bool_Exp['_and'], GuidelineFragment>({
        baseFilters: [{
            talent_id: { _eq: currentUserId }
        }],
        clickFilters: [
            {
                _or: [
                    { status: { _eq: Guideline_Status_Enum.Active } }
                ]
            }
        ],
        searchFilterFields: [],
        searchFilterRelations: {},
        clickFilterField: 'status',
        clickFiltersDisableDefaultAll: true,
        clickFilterDefault: 'active',
        clickFilterValues: {
            active: { label: 'Active', filter: [{ status: { _eq: Guideline_Status_Enum.Active } }] },
            inactive: { label: 'Inactive', filter: [{ status: { _eq: Guideline_Status_Enum.Inactive } }] },
            all: { label: 'All', filter: [] }
        }
    })

    React.useEffect(() => {
        subscribeToItems(filters);
        return () => {
            unsubscribeFromItems()
        }
    }, [filters, limit, offset, sortKey, sortOrder, subscribeToItems, unsubscribeFromItems, resetModule])

    const columnHelper = createColumnHelper<GuidelineFragment>()

    const columns = React.useMemo(() => [
        columnHelper.accessor('id', {
            header: 'Id'
        }),
        columnHelper.accessor('status', {
            header: 'Status',
            cell: row => capitalizeWord(row.getValue())
        }),
        columnHelper.accessor(row => row.guideline_role?.display, {
            id: 'guideline_role',
            meta: {
                relationKey: 'guideline_role',
                sortKey: 'value'
            },
            header: 'Role'
        }),
        columnHelper.accessor('target_booking_status', {
            header: 'Target Status',
            cell: row => row.getValue() ? capitalizeWord(row.getValue()!) : 'All Stages'
        }),
        columnHelper.accessor('target_booking_type', {
            header: 'Target Type',
            cell: row => row.getValue() ? capitalizeWord(row.getValue()!) : 'All Types'
        }),
        columnHelper.accessor('name', {
            header: 'Name'
        }),
        columnHelper.accessor('description', {
            header: 'Description'
        }),
        columnHelper.accessor('created_at', {
            header: 'Created At',
            cell: row => formatDate(row.getValue(), 'MM/DD/YY')
        }),
        columnHelper.accessor('updated_at', {
            header: 'Updated At',
            cell: row => formatDate(row.getValue(), 'MM/DD/YY')
        }),
    ], [columnHelper])

    const initialState = {
        columnVisibility: {
            id: false
        }
    }

    const handleMetaChange = (meta: SetMetaInput<GuidelineFragment>) => {
        setMeta(meta)
    }

    const handleSelect = (id: string) => {
        selectItem(id)
    }

    const renderCards = () => {
        return (
            <React.Fragment>
                <Grid>
                    {itemsList.map((i, k) => {
                        return (
                            <Grid.Col span={6} key={k}>
                                <Card
                                    component={Link}
                                    to={`/guidelines/${i.id}`}
                                    shadow="xs"
                                    className="card-theme-global"
                                >
                                    <Group justify='space-between'>
                                        <Text fw={500}>#{i.id}</Text>
                                        <Badge variant="outline">{i.status}</Badge>
                                    </Group>
                                    <Text size="sm">{i.id}</Text>
                                </Card>
                            </Grid.Col>
                        )
                    })}
                </Grid>
                <LoadMoreButton meta={meta} onLoadMore={getMoreItems} loading={loadingMore} />
            </React.Fragment>
        )
    }

    const renderList = () => {
        return (
            <DataTable<GuidelineFragment>
                columns={columns}
                data={itemsList}
                loading={loading}
                meta={meta}
                onMetaChange={handleMetaChange}
                initialState={initialState}
                onSelectItem={handleSelect}
            />
        )
    }

    return (
        <PageLayout
            header={
                <PageHeader
                    title={`Guidelines`}
                    renderAction={
                        <Group>
                            <UpdateOperationsAdminVenueTalentGuidelineAction />
                            <CreateOperationsAdminVenueTalentGuidelineAction />
                        </Group>
                    }
                />
            }
        >
            <OperationsAdminVenueTalentTabs/>
            <PageFilters options={filterParams} />
            <ModuleErrors errors={errors} />
            {itemsList.length ? (<> {view === 'list' ? renderList() : renderCards()} </>) : (<EmptyPageComponent label='Guidelines' />)}
        </PageLayout>
    )

}